/**
 * Coups de pouce — Attendu 659 « Propriété caractéristique de la médiatrice
 * (équidistance M ∈ méd([AB]) ⟺ MA = MB) ».
 *
 * Une entrée par `generator_key` (cf. gen_659.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *
 * Règle absolue : l'aide porte sur d'AUTRES lettres et d'AUTRES valeurs que
 * celles que le générateur peut tirer, sinon elle donne la réponse. Chaque coup
 * de pouce est autonome : les formats sont indépendants et un exercice peut ne
 * jamais être donné.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : l'équidistance lue sur la figure ──
// 15 cm : le générateur tire ses longueurs dans [2..12], la valeur est donc hors
// de portée et l'exemple ne peut pas servir de réponse. Deux codages distincts
// et de couleurs différentes : l'orange dit « (d) est la médiatrice de [RS] »
// (angle droit + moitiés égales), le rouge dit ce qui en découle, PR = PS.
const SVG_EQUIDISTANCE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 210" role="img"
     aria-label="Un segment RS, sa mediatrice d, et un point P place sur cette droite. Les deux longueurs PR et PS mesurent 15 cm.">
  <line x1="180" y1="22" x2="180" y2="178" stroke="#378ADD" stroke-width="1.6" stroke-dasharray="7 5"/>
  <text x="188" y="36" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <line x1="90" y1="155" x2="270" y2="155" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="180" y1="60" x2="90" y2="155" stroke="#0f172a" stroke-width="1.8"/>
  <line x1="180" y1="60" x2="270" y2="155" stroke="#0f172a" stroke-width="1.8"/>

  <polyline points="192,155 192,143 180,143" fill="none" stroke="#F97316" stroke-width="1.6"/>
  <line x1="135" y1="149" x2="135" y2="161" stroke="#F97316" stroke-width="1.8"/>
  <line x1="225" y1="149" x2="225" y2="161" stroke="#F97316" stroke-width="1.8"/>

  <line x1="131.4" y1="104.1" x2="138.6" y2="110.9" stroke="#E24B4A" stroke-width="2"/>
  <line x1="221.4" y1="110.9" x2="228.6" y2="104.1" stroke="#E24B4A" stroke-width="2"/>

  <circle cx="90" cy="155" r="4" fill="#0f172a"/>
  <circle cx="270" cy="155" r="4" fill="#0f172a"/>
  <circle cx="180" cy="60" r="4" fill="#0f172a"/>
  <text x="80" y="174" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="276" y="174" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="171" y="52" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">P</text>

  <text x="126" y="104" text-anchor="end" font-size="12" font-weight="700" fill="#E24B4A">15 cm</text>
  <text x="234" y="104" text-anchor="start" font-size="12" font-weight="700" fill="#E24B4A">15 cm</text>

  <text x="180" y="199" text-anchor="middle" font-size="12.5" font-weight="700" fill="#0f172a">P est sur (d), donc PR = PS</text>
</svg>`.trim();

// ── Figure Ex. 2 : la médiatrice comme ENSEMBLE de points ──
// Le distracteur qui fait tomber les élèves est « M est le milieu de [AB] ».
// La figure montre donc trois points de la médiatrice, tous équidistants de R
// et de S, dont le milieu : on voit que le milieu n'est qu'un cas parmi
// d'autres. Une couleur par point, pour que les trois égalités ne se
// confondent pas.
const SVG_ENSEMBLE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 245" role="img"
     aria-label="Un segment RS, sa mediatrice, et trois points places dessus : un au-dessus, le milieu du segment, un en dessous. Chacun est a egale distance de R et de S.">
  <line x1="180" y1="20" x2="180" y2="215" stroke="#378ADD" stroke-width="1.6" stroke-dasharray="7 5"/>
  <text x="188" y="33" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <line x1="180" y1="55" x2="80" y2="130" stroke="#0f172a" stroke-width="1.4"/>
  <line x1="180" y1="55" x2="280" y2="130" stroke="#0f172a" stroke-width="1.4"/>
  <line x1="180" y1="195" x2="80" y2="130" stroke="#0f172a" stroke-width="1.4"/>
  <line x1="180" y1="195" x2="280" y2="130" stroke="#0f172a" stroke-width="1.4"/>

  <line x1="80" y1="130" x2="280" y2="130" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>

  <line x1="127" y1="88.5" x2="133" y2="96.5" stroke="#E24B4A" stroke-width="2"/>
  <line x1="227" y1="96.5" x2="233" y2="88.5" stroke="#E24B4A" stroke-width="2"/>
  <line x1="130" y1="123" x2="130" y2="137" stroke="#F97316" stroke-width="2"/>
  <line x1="230" y1="123" x2="230" y2="137" stroke="#F97316" stroke-width="2"/>
  <line x1="127.3" y1="166.7" x2="132.7" y2="158.3" stroke="#9333EA" stroke-width="2"/>
  <line x1="227.3" y1="158.3" x2="232.7" y2="166.7" stroke="#9333EA" stroke-width="2"/>

  <circle cx="80" cy="130" r="4" fill="#0f172a"/>
  <circle cx="280" cy="130" r="4" fill="#0f172a"/>
  <circle cx="180" cy="55" r="4.5" fill="#E24B4A"/>
  <circle cx="180" cy="130" r="4.5" fill="#F97316"/>
  <circle cx="180" cy="195" r="4.5" fill="#9333EA"/>

  <text x="68" y="149" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="288" y="149" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="171" y="48" text-anchor="end" font-size="12.5" font-weight="700" fill="#E24B4A">P</text>
  <text x="171" y="213" text-anchor="end" font-size="12.5" font-weight="700" fill="#9333EA">Q</text>
  <text x="190" y="149" font-size="11.5" font-weight="700" fill="#F97316">milieu</text>

  <text x="180" y="236" text-anchor="middle" font-size="11" font-weight="700" fill="#0f172a">tous les points de (d) sont à égale distance de R et de S</text>
</svg>`.trim();

// ── Figure Ex. 3 : sur la droite ou à côté ──
// L'exercice fait trancher point par point sur une figure. Ce qui décide, c'est
// uniquement l'appartenance à la médiatrice : les deux cas sont donc montrés
// côte à côte sur le MÊME segment, l'un dessus l'autre à côté. Q est placé
// franchement décalé pour que l'inégalité des deux longueurs se voie.
const SVG_DESSUS_ACOTE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 230" role="img"
     aria-label="Un segment RS et sa mediatrice. Le point P est sur la droite : PR est egal a PS. Le point Q est a cote de la droite : QR est plus court que QS.">
  <line x1="180" y1="20" x2="180" y2="200" stroke="#378ADD" stroke-width="1.6" stroke-dasharray="7 5"/>
  <text x="188" y="33" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <line x1="180" y1="62" x2="85" y2="140" stroke="#0f172a" stroke-width="1.4"/>
  <line x1="180" y1="62" x2="275" y2="140" stroke="#0f172a" stroke-width="1.4"/>
  <line x1="110" y1="190" x2="85" y2="140" stroke="#0f172a" stroke-width="1.4"/>
  <line x1="110" y1="190" x2="275" y2="140" stroke="#0f172a" stroke-width="1.4"/>

  <line x1="85" y1="140" x2="275" y2="140" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <polyline points="192,140 192,128 180,128" fill="none" stroke="#F97316" stroke-width="1.6"/>
  <line x1="132" y1="134" x2="132" y2="146" stroke="#F97316" stroke-width="1.8"/>
  <line x1="228" y1="134" x2="228" y2="146" stroke="#F97316" stroke-width="1.8"/>

  <line x1="129" y1="97" x2="135" y2="105" stroke="#1D9E75" stroke-width="2"/>
  <line x1="225" y1="105" x2="231" y2="97" stroke="#1D9E75" stroke-width="2"/>

  <circle cx="85" cy="140" r="4" fill="#0f172a"/>
  <circle cx="275" cy="140" r="4" fill="#0f172a"/>
  <circle cx="180" cy="62" r="4.5" fill="#1D9E75"/>
  <circle cx="110" cy="190" r="4.5" fill="#E24B4A"/>

  <text x="73" y="133" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="283" y="133" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="171" y="55" text-anchor="end" font-size="12.5" font-weight="700" fill="#1D9E75">P</text>
  <text x="101" y="205" text-anchor="end" font-size="12.5" font-weight="700" fill="#E24B4A">Q</text>

  <text x="196" y="60" font-size="11.5" font-weight="700" fill="#1D9E75">P est sur (d) : PR = PS</text>
  <text x="120" y="205" font-size="11.5" font-weight="700" fill="#E24B4A">Q est à côté : QR ≠ QS</text>
</svg>`.trim();

// ── Figure Ex. 4 : la propriété dans les deux sens ──
// L'exercice fait compléter la définition : toute règle écrite donnerait les
// mots à placer. L'aide reste donc une fiche. Les deux formulations sont
// posées dans deux cadres reliés par une double flèche, parce que c'est
// l'équivalence qui est en jeu et pas une implication. La fiche ne couvre pas
// le modèle « construction au compas » : arcs et rayon n'y tiendraient pas
// sans la rendre illisible.
const SVG_FICHE_PROPRIETE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 245" role="img"
     aria-label="Un segment RS, sa mediatrice, et un point P pose dessus avec PR egal a PS. Deux cadres relies par une double fleche : P est sur la mediatrice de RS, et PR egale PS.">
  <line x1="180" y1="18" x2="180" y2="150" stroke="#378ADD" stroke-width="1.6" stroke-dasharray="7 5"/>
  <text x="188" y="31" font-size="12.5" font-style="italic" font-weight="700" fill="#378ADD">(d)</text>

  <line x1="95" y1="118" x2="265" y2="118" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <line x1="180" y1="52" x2="95" y2="118" stroke="#0f172a" stroke-width="1.6"/>
  <line x1="180" y1="52" x2="265" y2="118" stroke="#0f172a" stroke-width="1.6"/>

  <polyline points="192,118 192,106 180,106" fill="none" stroke="#F97316" stroke-width="1.6"/>
  <line x1="137.5" y1="112" x2="137.5" y2="124" stroke="#F97316" stroke-width="1.8"/>
  <line x1="222.5" y1="112" x2="222.5" y2="124" stroke="#F97316" stroke-width="1.8"/>

  <line x1="134.4" y1="81.1" x2="140.6" y2="88.9" stroke="#E24B4A" stroke-width="2"/>
  <line x1="219.4" y1="88.9" x2="225.6" y2="81.1" stroke="#E24B4A" stroke-width="2"/>

  <circle cx="95" cy="118" r="4" fill="#0f172a"/>
  <circle cx="265" cy="118" r="4" fill="#0f172a"/>
  <circle cx="180" cy="52" r="4.5" fill="#378ADD"/>
  <text x="85" y="136" text-anchor="end" font-size="12.5" font-weight="700" fill="#0f172a">R</text>
  <text x="273" y="136" font-size="12.5" font-weight="700" fill="#0f172a">S</text>
  <text x="171" y="45" text-anchor="end" font-size="12.5" font-weight="700" fill="#378ADD">P</text>

  <rect x="14" y="172" width="151" height="42" rx="10" fill="#EFF6FF" stroke="#378ADD" stroke-width="1.4"/>
  <text x="89" y="189" text-anchor="middle" font-size="11" font-weight="700" fill="#378ADD">P est sur</text>
  <text x="89" y="204" text-anchor="middle" font-size="11" font-weight="700" fill="#378ADD">la médiatrice de [RS]</text>

  <rect x="222" y="172" width="124" height="42" rx="10" fill="#FEF2F2" stroke="#E24B4A" stroke-width="1.4"/>
  <text x="284" y="199" text-anchor="middle" font-size="15" font-weight="700" fill="#E24B4A">PR = PS</text>

  <line x1="173" y1="193" x2="214" y2="193" stroke="#0f172a" stroke-width="1.8"/>
  <polyline points="181,188 173,193 181,198" fill="none" stroke="#0f172a" stroke-width="1.8" stroke-linecap="round"/>
  <polyline points="206,188 214,193 206,198" fill="none" stroke="#0f172a" stroke-width="1.8" stroke-linecap="round"/>

  <text x="180" y="234" text-anchor="middle" font-size="11" font-weight="700" fill="#0f172a">l'un entraîne l'autre, dans les deux sens</text>
</svg>`.trim();

const CP_659 = {
    pc_mediatrice_sens_direct_saisie: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    '**Propriété** : si un point est sur la **médiatrice** de [AB], il est à la **même distance** de A et de B.',
                    'Autrement dit MA = MB, et le triangle MAB est **isocèle** en M.',
                    'Cela marche aussi dans l\'autre sens : si MA = MB, alors M est sur la médiatrice de [AB].',
                ],
            },
            { titre: 'Un exemple', svg: SVG_EQUIDISTANCE },
        ],
    },

    pc_mediatrice_sens_reciproque_qcm: {
        pages: [
            {
                titre: 'La règle',
                // Ancienne version : la propriété réciproque énoncée telle quelle,
                // c'est-à-dire l'option correcte recopiée. On la remplace par la
                // manipulation qui la fait découvrir.
                lignes: [
                    'Marque deux points sur un brouillon, puis cherche à la règle **plusieurs** endroits situés à égale distance des deux. Places-en cinq ou six.',
                    'Regarde comment ils se disposent les uns par rapport aux autres : ils dessinent quelque chose.',
                    'Un seul point ne décrit pas un ensemble : la bonne réponse doit contenir **tous** ceux que tu as placés, et aucun autre.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_ENSEMBLE },
        ],
    },

    pc_mediatrice_vf_figure_4points: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Un point est à **égale distance** de A et de B **exactement** quand il est sur la **médiatrice** de [AB].',
                    'Il suffit donc de regarder si le point est **posé sur la droite** : dessus, MA = MB ; à côté, non.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_DESSUS_ACOTE },
        ],
    },

    // Aucune règle écrite : la définition à compléter EST l'exercice. L'aide se
    // limite à la fiche, où la propriété est posée dans les deux sens.
    pc_mediatrice_vocabulaire_trous: {
        pages: [
            { titre: 'Un exemple', svg: SVG_FICHE_PROPRIETE },
        ],
    },

    // Une seule règle, pas de figure : les trois étapes de l'exercice sont déjà
    // écrites et guidées. Ce qui manque à l'élève, c'est le geste de l'outil et
    // le piège du rayon qu'on change entre les deux cercles.
    pc_mediatrice_construction_compas: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Place la pointe du compas sur un point, puis fais glisser pour choisir le rayon. **Garde le même rayon** pour les deux cercles.',
                ],
            },
        ],
    },
};

export default CP_659;
